import React from 'react';
import { Box, Typography, IconButton, Radio, FormControlLabel, Checkbox, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeStyle } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import CustomButton from '../Button/CustomButton';
import { TPublishStatus } from './reducer/reducer';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker';
import dayjs from 'dayjs';

interface AudienceModalProps {
    onClose: () => void;
    onBack: () => void;
    currentStatus: TPublishStatus;
    onSelectStatus: (status: TPublishStatus, date?: string, id?: string) => void;
    themeStyle: ThemeStyle;
}

const postPreference = [
    {
        id: 'publish',
        text: 'Publish Immediately - Immediately available to anyone on thspian',
    },
    {
        id: 'draft',
        text: 'Draft - Publish at a later date.',
    },
    {
        id: 'schedule',
        text: 'Scheduled Publish - Publish at a specified date',
        showDate: true,
    },
];

const StatusModal: React.FC<AudienceModalProps> = ({
    onClose,
    onBack,
    currentStatus = 'Publish Immediately - Immediately available to anyone on thspian',
    onSelectStatus,
    themeStyle,
}) => {
    const [selectedStatus, setSelectedStatus] = React.useState<TPublishStatus>(currentStatus);
    const [setDefaultStatus, setSetDefaultStatus] = React.useState(false);
    const [date, setDate] = React.useState<string | undefined>(dayjs().add(2, 'days').toISOString());

    const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedStatus(event.target.value as TPublishStatus);
        if (event.target.value !== 'Scheduled Publish - Publish at a specified date') {
            setDate(undefined);
        }
    };

    const handleDone = () => {
        const selectedId = postPreference.find(pref => pref.text === selectedStatus);
        onSelectStatus(selectedStatus, date, selectedId?.id);
        onBack();
    };

    return (
        <Box sx={{ width: '100%', flexShrink: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}>
                <IconButton onClick={onBack} sx={{ color: theme[themeStyle].tag_white }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    variant="h6"
                    style={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Post Preference
                </Typography>
                <Box />
            </Box>

            <Typography sx={{ mb: 2, color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                Who can see your post? Your post will show up in Feed, on your profile, and in search results.
            </Typography>

            {postPreference.map((pref, index) => (
                <FormControlLabel
                    value={pref.text}
                    key={index}
                    control={
                        <Radio
                            checked={selectedStatus === pref.text}
                            onChange={handleStatusChange}
                            value={pref.text}
                            sx={{
                                color: theme[themeStyle].tag_white,

                                '&.Mui-checked': {
                                    color: theme[themeStyle].highlight, // Color for checked state
                                },
                            }}
                        />
                    }
                    sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}
                    label={
                        <Stack direction={{ xs: 'column' }}>
                            <Typography sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                {pref.text}
                            </Typography>
                            {pref.showDate && selectedStatus === pref.text && (
                                <CustomDatePicker
                                    value={date}
                                    onChange={e => {
                                        setDate(e);
                                    }}
                                    minDate={dayjs()}
                                    withTime
                                />
                            )}
                        </Stack>
                    }
                />
            ))}

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                <Checkbox
                    checked={setDefaultStatus}
                    onChange={e => setSetDefaultStatus(e.target.checked)}
                    sx={{
                        color: theme[themeStyle].tag_white,
                        '&.Mui-checked': {
                            color: theme[themeStyle].highlight, // Color for checked state
                        },
                    }}
                />
                <Typography style={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    Set as default.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 3,
                }}>
                <CustomButton
                    label="Cancel"
                    onClick={handleDone}
                    buttonProps={{
                        variant: 'text',
                    }}
                />
                <CustomButton label="Done" onClick={handleDone} />
            </Box>
        </Box>
    );
};

export default StatusModal;
